import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

// Squarespace CDN images already ordered as the old /projects page had them,
// interleaved with a handful of Ottostumm | Mogs project references from the
// existing homepage asset library. Serve at 1500w for high-DPI displays.
const SS = (path) => `${path}?format=1500w`;

// Per-image object-position overrides for tiles whose subject sits off-centre
// in the source photo and would be sliced by a default 4:5 centre-crop.
const POS_OVERRIDES = {
  // 9th tile — DSCF1756 — door subject sits in the left-centre of a 3:2 frame,
  // default 50% 50% was cropping the door in half. Anchor the crop to the left.
  "https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/506675ba-f15b-4a64-8863-a42b4fc72c36/DSCF1756.jpeg?format=1500w":
    "20% 50%",
};

// Source images that were stored sideways (no EXIF orientation tag on the
// server) and need to be rotated 90° clockwise so the door/window sits upright
// in the 4:5 project tile.
const ROTATE_CW = new Set([
  "https://customer-assets-rejwkqb3.emergentagent.net/job_7c966369-8b86-4034-b768-cad248e60af9/artifacts/5uqm9hzc_IMG_3620.webp",
  "https://customer-assets-rejwkqb3.emergentagent.net/job_7c966369-8b86-4034-b768-cad248e60af9/artifacts/vkc0tyki_DSCF3975%202.webp",
  "https://customer-assets-rejwkqb3.emergentagent.net/job_7c966369-8b86-4034-b768-cad248e60af9/artifacts/19fa0yvw_DSCF3965%202.webp",
]);

// Same problem, opposite direction — these need 90° counter-clockwise.
const ROTATE_CCW = new Set([
  "https://customer-assets-rejwkqb3.emergentagent.net/job_7c966369-8b86-4034-b768-cad248e60af9/artifacts/ajtt99xg_DSCF5738%202.webp",
]);

const PROJECT_IMAGES = [
  // Recent Eurosteel NZ install — French doors to conservatory (weatherboard exterior)
  "https://customer-assets-cm19k8pv.emergentagent.net/job_79d410ce-bad2-4f45-b915-a6981676b1a1/artifacts/xk2xm5tw_IMG_4747.webp",
  // NEW — Fluted-glass awning window · interior close-up
  "https://customer-assets-rejwkqb3.emergentagent.net/job_7c966369-8b86-4034-b768-cad248e60af9/artifacts/rvo4bi1i_IMG_3573.webp",
  // 1
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/c803ea4f-67b6-4f71-a823-6481ee2dd3c8/DA5E8306-908F-4853-94E7-E9FBF1447A22+2.JPG"),
  // NEW — Open double doors to stair hallway (interior)
  "https://customer-assets-rejwkqb3.emergentagent.net/job_7c966369-8b86-4034-b768-cad248e60af9/artifacts/top9wlps_B2606E0B-E48E-4E20-82DC-3CB1BF5FD0BA.jpg",
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/3c3818c0-a545-4997-a2ca-6c52c4cc954a/IMG_1802.jpg"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/8c6bcc8f-85db-4dfe-85c3-782fc04f0b7d/DSCF1778.jpeg"),
  // Ottostumm feature 1
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_2ed0240b-8186-438f-82ff-c30b1e517b68/artifacts/4ojvhgoi_Ottostumm%20Mogs%20FerroFinestra%20%20B75%20TB%202.webp",
  // NEW — 6-lite internal window looking through corridor
  "https://customer-assets-rejwkqb3.emergentagent.net/job_7c966369-8b86-4034-b768-cad248e60af9/artifacts/ajtt99xg_DSCF5738%202.webp",
  // 4
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/291ce3bb-ff22-4389-b00b-9e97fa482e04/DSCF4219.JPG"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/2ca501b3-6ac0-4c5d-8186-111bc23cfb02/DSCF3972.JPG"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/89389316-ccb8-456d-abcf-592d8a2d78e7/DSCF4269.JPG"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/88452bd4-63a9-4480-b33e-24cb28edd0d1/IMG_1807.JPG"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/506675ba-f15b-4a64-8863-a42b4fc72c36/DSCF1756.jpeg"),
  // Ottostumm feature 2 (W20 residential)
  "https://customer-assets-rejwkqb3.emergentagent.net/job_5f9461bb-28a7-4bac-8192-aecb42807f6c/artifacts/comj7zx5_Ottostumm%20Mogs%20-%20FerroFinestra%20W20%20b.jpg",
  // NEW — Bronze pivot door with slim sidelite (build-in-progress)
  "https://customer-assets-rejwkqb3.emergentagent.net/job_7c966369-8b86-4034-b768-cad248e60af9/artifacts/facymdn3_DSCF5813%202.webp",
  // Ottostumm — W20 heritage restoration
  "https://customer-assets-v7afamib.emergentagent.net/job_3cc12260-5622-4383-abad-481c3375154e/artifacts/sjuqsk34_Ottostumm%20Mogs%20-%20FerroFinestra%20W20.jpg",
  // 10
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/a4bf10bd-751d-4fdd-8210-021c29b85a02/DSCF1776.jpeg"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/2dc9c66d-5d6e-4b83-8714-4c5f3d71cc5c/DSCF1183+2.JPG"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/34574cc6-b5a4-4a8e-9faa-f48eb702e32d/IMG_1800.JPG"),
  // NEW — Arched interior transom with fluted glass
  "https://customer-assets-rejwkqb3.emergentagent.net/job_7c966369-8b86-4034-b768-cad248e60af9/artifacts/5uqm9hzc_IMG_3620.webp",
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/518b0cc5-97ee-4372-a0c5-50d938ee558c/DSCF1242+2.jpg"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/08cff08d-9c6e-4076-866c-5e881c968f3a/DSCF1170+2.JPG"),
  // Ottostumm feature 3 (W50)
  "https://customer-assets-rejwkqb3.emergentagent.net/job_5f9461bb-28a7-4bac-8192-aecb42807f6c/artifacts/itfrbrs8_Ottostumm%20Mogs%20FerroFinestra%20%20W50.jpg",
  // NEW — White weatherboard exterior · corner steel window + French door
  "https://customer-assets-rejwkqb3.emergentagent.net/job_7c966369-8b86-4034-b768-cad248e60af9/artifacts/19fa0yvw_DSCF3965%202.webp",
  // Ottostumm — Hillside Pavilion (W50 exterior)
  "https://customer-assets-v7afamib.emergentagent.net/job_fe0c775c-382e-4bf1-ab8e-bb795dea130b/artifacts/yj42hevn_Ottostumm%20Mogs%20FerroFinestra%20%20W50.jpg",
  // 16
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/3760c45f-4d5e-4090-8cfb-0e632d759700/DSCF1263.jpg"),
  // NEW — Vertical shiplap facade · 6-lite steel window + clerestory
  "https://customer-assets-rejwkqb3.emergentagent.net/job_7c966369-8b86-4034-b768-cad248e60af9/artifacts/erwhkj16_DSCF3960%202.webp",
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/d27ded05-e709-4d1d-a44f-60070d894321/DSCF1225+2.JPG"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/b411f9cb-0dde-44e7-b78e-e49ef95a2084/DSCF1173+2.jpg"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/c51e2873-eee9-4cc6-8d7e-eea6296b5e68/DSCF3965.JPG"),
  // NEW — Black steel entry detail · half-moon pull hardware close-up
  "https://customer-assets-rejwkqb3.emergentagent.net/job_7c966369-8b86-4034-b768-cad248e60af9/artifacts/vkc0tyki_DSCF3975%202.webp",
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/c0dd7f7b-8b46-425e-b3aa-154d053f5972/DSCF4198.JPG"),
  // Ottostumm feature 4 (W40)
  "https://customer-assets-rejwkqb3.emergentagent.net/job_5f9461bb-28a7-4bac-8192-aecb42807f6c/artifacts/t3a2w0i4_Ottostumm%20Mogs%20-%20FerroFinestra%20W40%20b.jpg",
  // Ottostumm — W40 & W75 references
  "https://customer-assets-rejwkqb3.emergentagent.net/job_5f9461bb-28a7-4bac-8192-aecb42807f6c/artifacts/y64khv2b_Ottostumm%20Mogs%20-%20FerroFinestra%20W40.jpg",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_2ed0240b-8186-438f-82ff-c30b1e517b68/artifacts/lam852jj_Ottostumm%20Mogs%20FerroFinestra%20W75%20%281%29%20%281%29.jpg",
  // 22
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/30f54c2c-1970-4f0d-954a-7d1956ffda54/DSCF1224+2.JPG"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/45027fc4-a9fc-41ba-9672-77f4360676d6/IMG_1808.JPG"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/88e50518-48f1-48b2-9208-8d0c5c63b739/DSCF1138+2.JPG"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/edb1afbf-4a4c-4c06-97c5-4e77dfc87881/DSCF1742.jpeg"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/47a98ce4-e12e-4710-973f-743ce92109ec/DSCF3971.JPG"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/9c905362-998f-4eba-baa5-ab9fe8e3c56d/DSCF4215.JPG"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/caab7e2a-6986-4d6a-9e15-9954e32f8985/DSCF4228.JPG"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/48cd3120-a986-4d46-bd4c-024f5145e3a9/DSCF4278.JPG"),
  SS("https://images.squarespace-cdn.com/content/v1/66779fe409a6942ed144de55/474476b4-9b9c-49fa-a1ad-5406aff5e1ef/DSCF4296.JPG"),
];

export default function Projects() {
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const openLightbox = (i) => setLightboxIdx(i);
  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const prev = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i - 1 + PROJECT_IMAGES.length) % PROJECT_IMAGES.length)),
    [],
  );
  const next = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i + 1) % PROJECT_IMAGES.length)),
    [],
  );

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxIdx, closeLightbox, prev, next]);

  return (
    <div data-testid="projects-page" className="bg-[#0a0a0c] text-white">
      {/* Page hero */}
      <section className="relative min-h-[56vh] w-full overflow-hidden">
        <img
          src="https://customer-assets-eiarnc6j.emergentagent.net/job_c8c33088-e275-4c7a-be4e-9570c082ca94/artifacts/5e98mml4_DSCF5696.webp"
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/95" />

        <div className="relative mx-auto flex min-h-[56vh] max-w-[1500px] flex-col justify-end px-6 pb-14 pt-40 lg:px-16 lg:pb-20 lg:pt-48">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.19, 1, 0.22, 1] }}
            className="font-serif-display text-6xl font-light leading-[1] tracking-[-0.02em] lg:text-8xl"
          >
            Projects.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.19, 1, 0.22, 1] }}
            className="mt-6 max-w-xl font-body text-[15px] leading-[1.7] text-white/75 lg:text-base"
          >
            A curated look at steel window and door work — from intimate entrances and heritage
            restorations through to full-house glazing and commercial fitouts.
          </motion.p>
        </div>
      </section>

      {/* Uniform grid — clean, editorial */}
      <section className="mx-auto max-w-[1600px] px-2 pb-24 pt-4 sm:px-4 lg:px-6">
        <div
          data-testid="projects-grid"
          className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3"
        >
          {PROJECT_IMAGES.map((src, i) => (
            <motion.figure
              key={src}
              data-testid={`project-image-${i}`}
              onClick={() => openLightbox(i)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.9,
                delay: (i % 3) * 0.05,
                ease: [0.19, 1, 0.22, 1],
              }}
              className="group relative cursor-pointer overflow-hidden bg-[#0e0e10]"
            >
              <div className="aspect-[4/5] w-full overflow-hidden">
                {ROTATE_CW.has(src) || ROTATE_CCW.has(src) ? (
                  <div
                    data-testid={`project-image-${i}-rotated`}
                    className="h-full w-full"
                    style={{
                      transform: `rotate(${ROTATE_CCW.has(src) ? "-90deg" : "90deg"}) scale(1.25)`,
                    }}
                  >
                    <img
                      src={src}
                      alt=""
                      loading="lazy"
                      style={{
                        objectPosition: POS_OVERRIDES[src] || "50% 50%",
                        imageOrientation: "from-image",
                      }}
                      className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
                    />
                  </div>
                ) : (
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    style={{
                      objectPosition: POS_OVERRIDES[src] || "50% 50%",
                      imageOrientation: "from-image",
                    }}
                    className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
                  />
                )}
              </div>
            </motion.figure>
          ))}
        </div>

        <div className="mt-24 flex justify-center">
          <Link
            to="/contact"
            data-testid="projects-cta"
            className="rounded-full bg-[#c6a87c] px-10 py-4 font-tech text-[11px] uppercase tracking-[0.3em] text-[#0a0a0c] transition-all duration-500 hover:bg-[#d8bd93]"
          >
            Start your project
          </Link>
        </div>
      </section>

      <ProjectLightbox
        idx={lightboxIdx}
        onClose={closeLightbox}
        onPrev={prev}
        onNext={next}
      />
    </div>
  );
}

function ProjectLightbox({ idx, onClose, onPrev, onNext }) {
  const isOpen = idx !== null;
  const src = isOpen ? PROJECT_IMAGES[idx] : null;
  const rotated = src ? ROTATE_CW.has(src) : false;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="lightbox"
          data-testid="project-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          className="fixed inset-0 z-[120] flex items-center justify-center bg-[#0a0a0c]/95 backdrop-blur-sm"
        >
          {/* Close */}
          <button
            type="button"
            data-testid="project-lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white/80 transition-colors hover:border-[#c6a87c] hover:text-[#c6a87c] lg:right-8 lg:top-8"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.6} />
          </button>

          {/* Prev */}
          <button
            type="button"
            data-testid="project-lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            className="absolute left-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white/80 transition-colors hover:border-[#c6a87c] hover:text-[#c6a87c] lg:left-8 lg:h-14 lg:w-14"
            aria-label="Previous"
          >
            <ChevronLeft size={22} strokeWidth={1.6} />
          </button>

          {/* Next */}
          <button
            type="button"
            data-testid="project-lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white/80 transition-colors hover:border-[#c6a87c] hover:text-[#c6a87c] lg:right-8 lg:h-14 lg:w-14"
            aria-label="Next"
          >
            <ChevronRight size={22} strokeWidth={1.6} />
          </button>

          {/* Image */}
          <motion.div
            key={src}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="flex h-full w-full items-center justify-center px-16 py-16 lg:px-24 lg:py-20"
          >
            {rotated || ROTATE_CCW.has(src) ? (
              <img
                src={src}
                alt=""
                data-testid="project-lightbox-image"
                style={{
                  transform: `rotate(${ROTATE_CCW.has(src) ? "-90deg" : "90deg"})`,
                  imageOrientation: "from-image",
                }}
                className="max-h-[75vh] max-w-[75vw] object-contain"
              />
            ) : (
              <img
                src={src}
                alt=""
                data-testid="project-lightbox-image"
                style={{ imageOrientation: "from-image" }}
                className="max-h-full max-w-full object-contain"
              />
            )}
          </motion.div>

          {/* Counter */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 font-tech text-[10px] uppercase tracking-[0.3em] text-white/50">
            {idx + 1} / {PROJECT_IMAGES.length}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
